<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
    ComboBox,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      activeTab: 'sdk',
      manualForm: {
        pageId: '',
        pageAccessToken: '',
        userAccessToken: '',
        inboxName: '',
      },
      isCreatingManual: false,
    };
  },

  validations: {
    pageName: {
      required,
    },

    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    comboBoxPageOptions() {
      return this.getSelectablePages.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        this.runFBInit(); // run init anyway, `tryFBlogin` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(); // make an attempt to login
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          // if the error was related to script loading, we show a toast
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          // if the error was anything else, we capture it and show a toast
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    setPageName(pageId) {
      const page = this.pageList.find(p => p.id === pageId);
      if (page) {
        this.selectedPage = page;
        this.pageName = page.name;
      } else {
        this.selectedPage = { name: null, id: null };
        this.pageName = '';
      }
      this.v$.selectedPage.$touch();
    },

    initChannelAuth(channel) {
      if (channel === 'facebook') {
        this.loadFBsdk();
      }
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope:
            'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages',
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // Ignore error
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name?.trim(),
      };
    },

    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(() => {
            this.isCreating = false;
          });
      }
    },

    async createChannelManual() {
      const { pageId, pageAccessToken, userAccessToken, inboxName } =
        this.manualForm;
      if (!pageId || !pageAccessToken || !userAccessToken || !inboxName) {
        useAlert(this.$t('INBOX_MGMT.ADD.FB.MANUAL.VALIDATION_ERROR'));
        return;
      }
      this.isCreatingManual = true;
      try {
        const data = await this.$store.dispatch('inboxes/createFBChannel', {
          page_id: pageId,
          page_access_token: pageAccessToken,
          user_access_token: userAccessToken,
          inbox_name: inboxName.trim(),
        });
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch {
        useAlert(this.$t('INBOX_MGMT.ADD.FB.MANUAL.ERROR'));
      } finally {
        this.isCreatingManual = false;
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <!-- Tabs -->
    <div class="flex gap-1 mb-6 border-b border-n-slate-6">
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'sdk'
            ? 'text-n-brand border-b-2 border-n-brand -mb-px'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'sdk'"
      >
        {{ $t('INBOX_MGMT.ADD.FB.TAB_SDK') }}
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'manual'
            ? 'text-n-brand border-b-2 border-n-brand -mb-px'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'manual'"
      >
        {{ $t('INBOX_MGMT.ADD.FB.TAB_MANUAL') }}
      </button>
    </div>

    <!-- SDK tab -->
    <div v-if="activeTab === 'sdk'">
      <div
        v-if="!hasLoginStarted"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <a href="#" @click="startLogin()">
          <img
            class="w-auto h-10 rounded-md"
            src="~dashboard/assets/images/channels/facebook_login.png"
            alt="Facebook-logo"
          />
        </a>
        <p class="py-6">
          {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
        </p>
      </div>
      <div v-else>
        <div v-if="hasError" class="max-w-lg mx-auto text-center">
          <h5>{{ errorStateMessage }}</h5>
          <p
            v-if="errorStateDescription"
            v-dompurify-html="errorStateDescription"
          />
        </div>
        <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
        <form
          v-else
          class="flex flex-col flex-wrap mx-0"
          @submit.prevent="createChannel()"
        >
          <div class="w-full">
            <PageHeader
              :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
              :header-content="
                replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC'))
              "
            />
          </div>
          <div class="w-3/5">
            <div class="w-full mb-2">
              <div
                class="input-wrap"
                :class="{ error: v$.selectedPage.$error }"
              >
                <span class="text-n-slate-12 text-start">
                  {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
                </span>
                <ComboBox
                  :model-value="selectedPage.id"
                  :options="comboBoxPageOptions"
                  :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                  :has-error="v$.selectedPage.$error"
                  class="[&>div>button]:!bg-n-alpha-black2 mt-1"
                  @update:model-value="setPageName"
                />
                <span v-if="v$.selectedPage.$error" class="message mt-0.5">
                  {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
                </span>
              </div>
            </div>
            <div class="w-full">
              <label :class="{ error: v$.pageName.$error }">
                {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
                <input
                  v-model="pageName"
                  type="text"
                  :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
                  @input="v$.pageName.$touch"
                />
                <span v-if="v$.pageName.$error" class="message">
                  {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
                </span>
              </label>
            </div>
            <div class="w-full text-right">
              <NextButton :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')" />
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Manual tab -->
    <div v-if="activeTab === 'manual'" class="max-w-lg">
      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.FB.MANUAL.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.FB.MANUAL.DESCRIPTION')"
      />
      <div class="flex flex-col gap-4 mt-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.FB.MANUAL.INBOX_NAME') }}
          <input
            v-model="manualForm.inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.FB.MANUAL.INBOX_NAME_PLACEHOLDER')"
          />
        </label>
        <label>
          {{ $t('INBOX_MGMT.ADD.FB.MANUAL.PAGE_ID') }}
          <input
            v-model="manualForm.pageId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.FB.MANUAL.PAGE_ID_PLACEHOLDER')"
          />
        </label>
        <label>
          {{ $t('INBOX_MGMT.ADD.FB.MANUAL.PAGE_ACCESS_TOKEN') }}
          <input
            v-model="manualForm.pageAccessToken"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.FB.MANUAL.PAGE_ACCESS_TOKEN_PLACEHOLDER')
            "
          />
        </label>
        <label>
          {{ $t('INBOX_MGMT.ADD.FB.MANUAL.USER_ACCESS_TOKEN') }}
          <input
            v-model="manualForm.userAccessToken"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.FB.MANUAL.USER_ACCESS_TOKEN_PLACEHOLDER')
            "
          />
        </label>
        <div class="text-right">
          <NextButton
            :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')"
            :is-loading="isCreatingManual"
            @click="createChannelManual"
          />
        </div>
      </div>
    </div>
  </div>
</template>
